<template>
  <div v-on:keyup="handleChange">
    <el-autocomplete
      style="width: 100%;"
      :placeholder="properties.placeholder"
      @select="handleSelect"
      @change="handleChange"
      :name="properties.filterName"
      :fetch-suggestions="properties.remote
        ? querySearchAsync
        : (query, cb) => autocompleteLocalSearch({ query, ref: `${properties.filterName}_autocomplete_filter`, field: properties }, cb)"
      :props="{ label: 'label', value: 'value' }"
      :trigger-on-focus="properties.triggerOnFocus"
      :select-when-unmatched="false"
      :icon="clearable && keyword ? 'circle-cross' : (clearable ? 'search' : null)"
      :on-icon-click="clearable && keyword ? clearKeyword : null"
      :ref="`${properties.filterName}_autocomplete_filter`"
      v-model="keyword">
    </el-autocomplete>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { searchMixins } from '@/Mixins/SearchMixins'

  export default {
    name: 'autocompleteFilter',
    props: {
      properties: Object
    },
    mixins: [ searchMixins ],
    data () {
      return {
        keyword: '',
        searchTimeout: null,
        suggestions: [],
        selection: {}
      }
    },
    methods: {
      /*
      * Searches for suggestions based on the keyword
      */
      querySearchAsync (query, cb) {
        let params = {}
        const { queryParamType, action, getter, suggestionLabelAttr } = this.properties
        params[queryParamType] = query
        let results = query ? this.suggestions.filter(this.createFilter(query)) : this.suggestions
        if (query && query !== '' && query.length > 0) {
          this.$store.dispatch(action, params)
            .then(r => {
              if (!(r && r.count)) {
                return cb([{ label: 'No data', value: '' }])
              }
              results = this.$store.getters[getter](query).map(v => {
                let ret = {}
                ret.label = v[suggestionLabelAttr]
                ret.value = v.id
                return ret
              })
              cb(results)
            })
            .catch(e => console.log(e))
        }
      },
      /**
       * Highlights the first autocomplete result after search
       */
      highlightFirstAutocompleteResult (ref) {
        const { $refs } = this
        const highlight = (r) => {
          if (r.includes('autocomplete') && $refs[r] && $refs[r].suggestions && $refs[r].suggestions.length) {
            this.$refs[r].highlight(0)
          }
        }
        if (ref) {
          highlight(ref)
        } else {
          for (let r in $refs) {
            highlight(r)
          }
        }
      },
      /*
      * Creates the filter responsible for displaying suggestions
      * @param query
      */
      createFilter (query) {
        return (link) => link.value.indexOf(query.toLowerCase())
      },
      /*
      * Handles what happens when a suggestion is selected
      * @param selection
      */
      handleSelect (selection) {
        if (selection && selection.value) {
          if (typeof this.properties.sync === 'function') {
            this.properties.sync(this.properties.filterName, selection.value)
            if (this.properties.applyOnSelection) {
              this.properties.apply()
            }
          }
        }
        this.selection = selection
        this.keyword = selection.label
        if (this.properties.clearAction) {
          this.$store.dispatch(this.properties.clearAction)
        }
      },
      /*
      * Handles changes in the filter's keyword criteria. When a change occurs, whatever
      * filter was previously set is cleared, and the selection object is cleared as well.
      */
      handleChange () {
        if (this.keyword !== this.selection.label) {
          this.$store.dispatch('unsetFilter', this.properties.filterName)
        }
        this.selection = Object.assign({})
      },
      /*
      * Clears the filter, as well as the selection, and unsets the filter in the filter store
      */
      clearKeyword () {
        this.keyword = ''
        this.selection = {}
        this.$store.dispatch('unsetFilter', this.properties.filterName)
        if (this.properties.clearAction) {
          this.$store.dispatch(this.properties.clearAction)
        }
        if (typeof this.properties.clear === 'function') {
          this.properties.clear(['autocomplete'])
        }
      },
      /*
      * Set a preselected autocomplete search criteria. Useful if list has already been filtered prior
      * to init render, and that filter criteria should be shown to the user.
      */
      setDefaultSelection () {
        const { filterName } = this.properties
        if (this.defaultSelection && this.defaultSelection[filterName]) {
          this.keyword = this.defaultSelection[filterName].label
          this.handleSelect(this.defaultSelection[filterName])
        } else {
          this.keyword = ''
          this.handleSelect({ label: '', value: '' })
        }
      }
    },
    computed: {
      ...mapGetters({
        defaultSelection: 'getDefaultSelection'
      }),
      /**
       * Returns a boolean indicating whether or not this filter is clearable. Defaults to 'false' if not
       * explicitly set.
       * @return {boolean}
       */
      clearable () {
        if (this.properties && this.properties.hasOwnProperty('clearable')) {
          return !!this.properties.clearable
        }
        return false
      }
    },
    watch: {
      defaultSelection () {
        this.setDefaultSelection()
      }
    },
    created () {
      this.setDefaultSelection()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/FilterComponents/AutocompleteFilter.vue